import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, TextField, CircularProgress, InputAdornment } from '@mui/material';
import { couponService } from '../services/couponService';
import { campaignService } from '../services/campaignService';
import { useToast } from '../contexts/ToastContext';
import { Campaign, Store } from '../types';
import { Icon } from '../components/participant/Icon';
import { ParticipantTopBar } from '../components/participant/ParticipantTopBar';
import { participantColors as c, fadeUpKeyframes } from '../theme/participantTheme';

const wizardKeyframes = `
${fadeUpKeyframes}
@keyframes participantPopIn {
  0% { transform: scale(0.4); opacity: 0; }
  70% { transform: scale(1.08); }
  100% { transform: scale(1); opacity: 1; }
}
@keyframes participantConfettiFall {
  0% { transform: translateY(-40px) rotate(0deg); opacity: 1; }
  100% { transform: translateY(105vh) rotate(720deg); opacity: 0.6; }
}
`;

const CONFETTI_COLORS = c.confetti;

const Confetti: React.FC = () => {
  const pieces = React.useMemo(
    () =>
      Array.from({ length: 40 }, (_, i) => ({
        left: (i * 100) / 40 + (i % 3),
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        duration: 2.6 + (i % 5) * 0.4,
        delay: (i % 7) * 0.2,
        size: 6 + (i % 4) * 2,
      })),
    []
  );

  return (
    <Box sx={{ position: 'fixed', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 1400 }}>
      {pieces.map((p, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            top: 0,
            left: `${p.left}%`,
            width: p.size,
            height: p.size,
            background: p.color,
            borderRadius: i % 2 === 0 ? '50%' : '2px',
            animation: `participantConfettiFall ${p.duration}s ease-in ${p.delay}s both`,
          }}
        />
      ))}
    </Box>
  );
};

const MyCouponFormSimple: React.FC = () => {
  const navigate = useNavigate();
  const { id: campaignIdFromUrl } = useParams<{ id: string }>();
  const { showError } = useToast();

  const [step, setStep] = useState(1); // 1: Campanha, 2: Loja, 3: Cupom, 4: Sucesso
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [couponNumber, setCouponNumber] = useState('');
  const [sellerName, setSellerName] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [newTotalCoupons, setNewTotalCoupons] = useState<number | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const campaignsData = await campaignService.getActiveCampaigns();
        setCampaigns(campaignsData || []);

        if (campaignIdFromUrl) {
          const campaign = campaignsData?.find((cmp) => cmp.id === campaignIdFromUrl);
          if (campaign) {
            setSelectedCampaign(campaign);
            setStores(campaign.stores || []);
            setStep(2);
          }
        }
      } catch (error) {
        showError('Erro ao carregar promoções');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [campaignIdFromUrl, showError]);

  const filteredStores = stores.filter((store) => store.name.toLowerCase().includes(searchTerm.toLowerCase()));

  const handleSelectCampaign = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setStores(campaign.stores || []);
    setStep(2);
  };

  const handleSelectStore = (store: Store) => {
    setSelectedStore(store);
    setStep(3);
  };

  const handleBack = () => {
    if (step === 2) {
      setSelectedCampaign(null);
      setStores([]);
      setSearchTerm('');
    } else if (step === 3) {
      setSelectedStore(null);
      setCouponNumber('');
      setSellerName('');
      setAcceptTerms(false);
    }
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    if (!couponNumber || !acceptTerms) {
      showError('Preencha o número do cupom e aceite o regulamento');
      return;
    }

    try {
      setSubmitting(true);
      await couponService.createCoupon({
        number: parseInt(couponNumber),
        campaignId: selectedCampaign!.id,
        storeId: selectedStore!.id,
        sellerName: sellerName || undefined,
      });

      try {
        // Só dessa campanha (a que o cupom acabou de ser cadastrado) — mesmo
        // problema do contador do topbar: sem o filtro, somava cupons de
        // todas as campanhas já encerradas também.
        const { total } = await couponService.getMyCoupons({
          limit: 1,
          campaignId: selectedCampaign!.id,
        });
        setNewTotalCoupons(total);
      } catch {
        setNewTotalCoupons(null);
      }

      setStep(4);
    } catch (error: any) {
      if (error.response?.status === 409) {
        showError('Este número de cupom já foi cadastrado');
      } else if (error.response?.status === 400) {
        showError(error.response.data.message || 'Dados inválidos');
      } else {
        showError('Erro ao cadastrar cupom');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const resetWizard = () => {
    setStep(1);
    setSelectedCampaign(null);
    setSelectedStore(null);
    setCouponNumber('');
    setSellerName('');
    setAcceptTerms(false);
    setSearchTerm('');
    setNewTotalCoupons(null);
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', background: c.gradients.pageBg }}>
        <CircularProgress sx={{ color: c.brandMid }} size={48} />
      </Box>
    );
  }

  const stepTitles: Record<number, { title: string; subtitle: string }> = {
    1: { title: 'Escolha a campanha', subtitle: 'Selecione qual promoção você quer participar' },
    2: { title: 'Escolha a loja', subtitle: 'Onde você fez a compra?' },
    3: { title: 'Número do cupom', subtitle: 'Digite o número que está no seu cupom fiscal' },
  };

  return (
    <Box sx={{ minHeight: '100vh', background: c.gradients.pageBg, fontFamily: c.fontFamily, pb: '48px' }}>
      <style>{wizardKeyframes}</style>

      <Box sx={{ maxWidth: 620, mx: 'auto', px: 2, display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {step === 4 && <Confetti />}

        {step < 4 ? (
          <>
            <ParticipantTopBar />

            {/* Cabeçalho de passo */}
            <Box
              sx={{
                background: c.surface,
                borderRadius: `${c.radius.card}px`,
                padding: '20px',
                boxShadow: c.shadows.card,
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
              }}
            >
              <Box
                component="button"
                onClick={() => (step > 1 ? handleBack() : navigate('/dashboard'))}
                title="Voltar"
                sx={{
                  width: 52,
                  height: 52,
                  flexShrink: 0,
                  border: `2px solid ${c.border}`,
                  borderRadius: `${c.radius.tile}px`,
                  background: c.surfaceHover,
                  color: c.brandDark,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': { background: '#e7f4e7', borderColor: c.borderHover },
                }}
              >
                <Icon name="arrow_back" size={24} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box component="div" sx={{ fontSize: 20, fontWeight: 800, color: c.textDarkest }}>
                  {stepTitles[step].title}
                </Box>
                <Box component="div" sx={{ fontSize: 14.5, color: c.textSecondary, mt: '2px' }}>
                  {stepTitles[step].subtitle}
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  gap: '4px',
                  flexShrink: 0,
                }}
              >
                {[1, 2, 3].map((n) => (
                  <Box
                    key={n}
                    sx={{
                      width: 24,
                      height: 6,
                      borderRadius: 3,
                      background: n <= step ? c.brandMid : c.border,
                      transition: 'background 0.2s',
                    }}
                  />
                ))}
              </Box>
            </Box>
          </>
        ) : null}

        {/* PASSO 1: Campanha */}
        {step === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {campaigns.length === 0 ? (
              <Box sx={{ background: c.surface, borderRadius: `${c.radius.card}px`, padding: '40px 24px', textAlign: 'center', boxShadow: c.shadows.card }}>
                <Icon name="campaign" size={52} color={c.iconMuted} />
                <Box component="div" sx={{ fontSize: 18, fontWeight: 800, color: c.textSecondary, mt: '12px' }}>
                  Nenhuma promoção ativa
                </Box>
                <Box
                  component="button"
                  onClick={() => navigate('/dashboard')}
                  sx={{
                    mt: '20px',
                    minHeight: 52,
                    px: 3,
                    border: `2px solid ${c.borderHover}`,
                    borderRadius: `${c.radius.button}px`,
                    background: c.surface,
                    color: c.brandDark,
                    fontFamily: c.fontFamily,
                    fontSize: 15,
                    fontWeight: 800,
                    cursor: 'pointer',
                  }}
                >
                  Voltar ao início
                </Box>
              </Box>
            ) : (
              campaigns.map((campaign, i) => (
                <Box
                  key={campaign.id}
                  component="button"
                  onClick={() => handleSelectCampaign(campaign)}
                  sx={{
                    textAlign: 'left',
                    cursor: 'pointer',
                    background: c.surface,
                    border: '2px solid transparent',
                    borderRadius: `${c.radius.card}px`,
                    boxShadow: '0 4px 20px rgba(20,61,24,0.07)',
                    padding: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    transition: 'border-color 0.15s, transform 0.15s',
                    animation: `participantFadeUp ${0.3 + i * 0.05}s ease both`,
                    '&:hover': { borderColor: c.brandMid, transform: 'translateY(-2px)' },
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      flexShrink: 0,
                      borderRadius: `${c.radius.tile}px`,
                      background: c.gradients.primaryButton,
                      color: c.onDarkPrimary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon name="emoji_events" size={32} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box component="div" sx={{ fontSize: 17.5, fontWeight: 800, color: c.textDarkest }}>
                      {campaign.name}
                    </Box>
                    {campaign.description && (
                      <Box component="div" sx={{ fontSize: 14, color: c.textSecondary, mt: '2px' }}>
                        {campaign.description.length > 70 ? `${campaign.description.substring(0, 70)}...` : campaign.description}
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', mt: '8px' }}>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          background: c.surfaceTint,
                          color: c.successText,
                          borderRadius: `${c.radius.pill}px`,
                          padding: '4px 10px',
                          fontSize: 12.5,
                          fontWeight: 700,
                        }}
                      >
                        <Icon name="storefront" size={14} />
                        {campaign.stores?.length || 0} lojas
                      </Box>
                    </Box>
                  </Box>
                  <Icon name="chevron_right" size={26} color={c.brandMid} />
                </Box>
              ))
            )}
          </Box>
        )}

        {/* PASSO 2: Loja */}
        {step === 2 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <TextField
              fullWidth
              placeholder="Buscar loja pelo nome..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon name="search" size={20} color={c.brandIcon} />
                  </InputAdornment>
                ),
              }}
              sx={{
                background: c.surface,
                '& .MuiOutlinedInput-root': { borderRadius: '18px', fontFamily: c.fontFamily },
              }}
            />

            {filteredStores.length === 0 ? (
              <Box sx={{ background: c.surface, borderRadius: `${c.radius.card}px`, padding: '40px 24px', textAlign: 'center', boxShadow: c.shadows.card }}>
                <Icon name="storefront" size={48} color={c.iconMuted} />
                <Box component="div" sx={{ fontSize: 17, fontWeight: 700, color: c.textSecondary, mt: '12px' }}>
                  Nenhuma loja encontrada
                </Box>
              </Box>
            ) : (
              filteredStores.map((store, i) => (
                <Box
                  key={store.id}
                  component="button"
                  onClick={() => handleSelectStore(store)}
                  sx={{
                    textAlign: 'left',
                    cursor: 'pointer',
                    background: c.surface,
                    border: '2px solid transparent',
                    borderRadius: `${c.radius.card}px`,
                    boxShadow: '0 4px 20px rgba(20,61,24,0.07)',
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    transition: 'border-color 0.15s, transform 0.15s',
                    animation: `participantFadeUp ${0.25 + i * 0.04}s ease both`,
                    '&:hover': { borderColor: c.brandMid, transform: 'translateY(-2px)' },
                  }}
                >
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      flexShrink: 0,
                      borderRadius: `${c.radius.tile}px`,
                      background: c.surfaceTint,
                      color: c.brandDark,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon name="storefront" size={26} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box component="div" sx={{ fontSize: 16.5, fontWeight: 700, color: c.textDarkest }}>
                      {store.name}
                    </Box>
                    {store.address && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mt: '2px' }}>
                        <Icon name="location_on" size={14} color={c.textTertiary} />
                        <Box component="span" sx={{ fontSize: 13.5, color: c.textTertiary }}>
                          {store.address}
                        </Box>
                      </Box>
                    )}
                  </Box>
                  <Icon name="chevron_right" size={24} color={c.brandMid} />
                </Box>
              ))
            )}
          </Box>
        )}

        {/* PASSO 3: Número do cupom */}
        {step === 3 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Resumo escuro */}
            <Box sx={{ background: c.surfaceDark, borderRadius: `${c.radius.card}px`, padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Box component="div" sx={{ fontSize: 12.5, fontWeight: 700, color: c.onDarkMuted, textTransform: 'uppercase' }}>
                    Campanha
                  </Box>
                  <Box component="div" sx={{ fontSize: 16, fontWeight: 800, color: c.onDarkPrimary, mt: '2px' }}>
                    {selectedCampaign?.name}
                  </Box>
                </Box>
                <Box
                  component="button"
                  onClick={() => {
                    setSelectedCampaign(null);
                    setSelectedStore(null);
                    setStores([]);
                    setStep(1);
                  }}
                  sx={{
                    border: 'none',
                    borderRadius: `${c.radius.pill}px`,
                    background: 'rgba(255,255,255,0.14)',
                    color: c.onDarkSecondary,
                    fontFamily: c.fontFamily,
                    fontSize: 12.5,
                    fontWeight: 700,
                    padding: '6px 12px',
                    cursor: 'pointer',
                  }}
                >
                  trocar
                </Box>
              </Box>
              <Box sx={{ height: '1px', background: 'rgba(255,255,255,0.14)' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Box component="div" sx={{ fontSize: 12.5, fontWeight: 700, color: c.onDarkMuted, textTransform: 'uppercase' }}>
                    Loja
                  </Box>
                  <Box component="div" sx={{ fontSize: 16, fontWeight: 800, color: c.onDarkPrimary, mt: '2px' }}>
                    {selectedStore?.name}
                  </Box>
                </Box>
                <Box
                  component="button"
                  onClick={handleBack}
                  sx={{
                    border: 'none',
                    borderRadius: `${c.radius.pill}px`,
                    background: 'rgba(255,255,255,0.14)',
                    color: c.onDarkSecondary,
                    fontFamily: c.fontFamily,
                    fontSize: 12.5,
                    fontWeight: 700,
                    padding: '6px 12px',
                    cursor: 'pointer',
                  }}
                >
                  trocar
                </Box>
              </Box>
            </Box>

            <Box sx={{ background: c.surface, borderRadius: `${c.radius.cardLarge}px`, padding: '22px', boxShadow: c.shadows.card, display: 'flex', flexDirection: 'column', gap: '18px' }}>
              <Box>
                <Box component="label" sx={{ fontSize: 15.5, fontWeight: 800, color: c.textDarkest, display: 'block', mb: '10px' }}>
                  Número do cupom
                </Box>
                <Box
                  component="input"
                  type="number"
                  placeholder="00000"
                  value={couponNumber}
                  onChange={(e) => setCouponNumber(e.target.value)}
                  sx={{
                    width: '100%',
                    textAlign: 'center',
                    border: `3px dashed ${couponNumber ? c.brandMid : c.borderHover}`,
                    borderRadius: `${c.radius.tile}px`,
                    background: couponNumber ? c.surfaceTint : c.surfaceHover,
                    fontFamily: c.fontFamily,
                    fontSize: 42,
                    fontWeight: 800,
                    letterSpacing: '0.14em',
                    color: c.textDarkest,
                    padding: '18px 8px',
                    outline: 'none',
                    transition: 'border-color 0.2s, background 0.2s',
                    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                  }}
                />
              </Box>

              <Box>
                <Box component="label" sx={{ fontSize: 15, fontWeight: 700, color: c.textDarkest, display: 'flex', alignItems: 'center', gap: '6px', mb: '8px' }}>
                  <Icon name="person" size={18} color={c.brandIcon} />
                  Nome do vendedor (opcional)
                </Box>
                <Box
                  component="input"
                  placeholder="Ex: João Silva"
                  value={sellerName}
                  onChange={(e) => setSellerName(e.target.value)}
                  sx={{
                    width: '100%',
                    border: `2px solid ${c.border}`,
                    borderRadius: `${c.radius.input}px`,
                    background: c.surface,
                    fontFamily: c.fontFamily,
                    fontSize: 16,
                    fontWeight: 600,
                    color: c.textBase,
                    padding: '14px 16px',
                    outline: 'none',
                  }}
                />
              </Box>

              <Box
                component="button"
                onClick={() => setAcceptTerms((v) => !v)}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  textAlign: 'left',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  padding: 0,
                  fontFamily: c.fontFamily,
                }}
              >
                <Box
                  sx={{
                    width: 26,
                    height: 26,
                    flexShrink: 0,
                    mt: '2px',
                    borderRadius: '8px',
                    border: `2px solid ${acceptTerms ? c.brandMid : '#b9d3bb'}`,
                    background: acceptTerms ? c.brandMid : c.surface,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background 0.2s, border-color 0.2s',
                  }}
                >
                  {acceptTerms && <Icon name="check" size={18} color={c.onDarkPrimary} />}
                </Box>
                <Box component="span" sx={{ fontSize: 15, color: c.textSecondary, lineHeight: 1.4 }}>
                  Li e aceito o{' '}
                  <Box
                    component="span"
                    sx={{ color: c.brandDark, fontWeight: 800 }}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      if (selectedCampaign?.regulation) window.open(selectedCampaign.regulation, '_blank');
                    }}
                  >
                    regulamento da promoção
                  </Box>
                </Box>
              </Box>

              <Box
                component="button"
                onClick={handleSubmit}
                disabled={!couponNumber || !acceptTerms || submitting}
                sx={{
                  width: '100%',
                  minHeight: 62,
                  border: 'none',
                  borderRadius: `${c.radius.button}px`,
                  fontFamily: c.fontFamily,
                  fontSize: 19,
                  fontWeight: 800,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  transition: 'transform 0.15s',
                  ...(couponNumber && acceptTerms && !submitting
                    ? {
                        background: c.gradients.primaryButton,
                        color: c.onDarkPrimary,
                        cursor: 'pointer',
                        boxShadow: c.shadows.primaryButton,
                        '&:hover': { transform: 'translateY(-2px)' },
                      }
                    : {
                        background: c.border,
                        color: c.textFaint,
                        cursor: 'not-allowed',
                      }),
                }}
              >
                {submitting ? (
                  <CircularProgress size={22} sx={{ color: 'inherit' }} />
                ) : (
                  <>
                    <Icon name="check_circle" size={24} />
                    Cadastrar cupom
                  </>
                )}
              </Box>
            </Box>
          </Box>
        )}

        {/* PASSO 4: Sucesso */}
        {step === 4 && (
          <Box
            sx={{
              background: c.surface,
              borderRadius: `${c.radius.cardLarge}px`,
              padding: '40px 28px',
              boxShadow: c.shadows.loginCard,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
              mt: '40px',
              animation: 'participantPopIn 0.5s ease both',
            }}
          >
            <Box
              sx={{
                width: 108,
                height: 108,
                borderRadius: '50%',
                background: c.gradients.successBadge,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: c.shadows.successBadge,
              }}
            >
              <Icon name="check_circle" size={56} color={c.onDarkPrimary} />
            </Box>

            <Box component="div" sx={{ fontSize: 26, fontWeight: 800, color: c.textDarkest }}>
              Cupom cadastrado!
            </Box>
            <Box component="div" sx={{ fontSize: 16, color: c.textSecondary, lineHeight: 1.5 }}>
              Seu cupom <strong>#{couponNumber}</strong> foi registrado na promoção <strong>{selectedCampaign?.name}</strong>
            </Box>

            {newTotalCoupons !== null && (
              <Box
                sx={{
                  width: '100%',
                  background: c.amberBg,
                  border: `2px solid ${c.amber}`,
                  borderRadius: `${c.radius.card}px`,
                  padding: '14px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                }}
              >
                <Icon name="confirmation_number" size={22} color={c.amber} />
                <Box component="span" sx={{ fontSize: 15, fontWeight: 700, color: c.amberTextDark }}>
                  Você já tem {newTotalCoupons} {newTotalCoupons === 1 ? 'cupom cadastrado' : 'cupons cadastrados'}
                </Box>
              </Box>
            )}

            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '12px', mt: '8px' }}>
              <Box
                component="button"
                onClick={resetWizard}
                sx={{
                  width: '100%',
                  minHeight: 58,
                  border: 'none',
                  borderRadius: `${c.radius.button}px`,
                  background: c.gradients.primaryButton,
                  color: c.onDarkPrimary,
                  fontFamily: c.fontFamily,
                  fontSize: 17,
                  fontWeight: 800,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxShadow: c.shadows.primaryButton,
                }}
              >
                <Icon name="add_circle" size={22} />
                Cadastrar outro cupom
              </Box>
              <Box
                component="button"
                onClick={() => navigate('/user/my-coupons')}
                sx={{
                  width: '100%',
                  minHeight: 58,
                  border: `2px solid ${c.borderHover}`,
                  borderRadius: `${c.radius.button}px`,
                  background: c.surface,
                  color: c.brandDark,
                  fontFamily: c.fontFamily,
                  fontSize: 17,
                  fontWeight: 800,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  '&:hover': { background: c.surfaceHover },
                }}
              >
                <Icon name="receipt_long" size={22} />
                Ver meus cupons
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MyCouponFormSimple;
